import { createSelector } from '@lumapps/redux/reselect';
import { BaseStore } from '@lumapps/redux/types';

import { LOADING_PAGE_ID } from '../constants';
import { RouterState, RouterStatus } from './types';

const internalRouterSelector = (state: BaseStore): RouterState => state.router || {};
const routerSelector = createSelector(internalRouterSelector, (router) => router);
const routerStatusSelector = createSelector(routerSelector, (router) => router.status);
const routerDataSelector = createSelector(routerSelector, (router) => router.data);
const routerIsLoading = createSelector(routerStatusSelector, (status) => status === RouterStatus.loading);
const blockedRedirectionSelector = createSelector(routerSelector, (router) => router.blockedRedirection);
const currentPageSelector = createSelector(routerSelector, (router) => router.currentPage);
const pageTitleSelector = createSelector(routerSelector, (router) => router.pageTitle);
// Generic pageContext selector
const pageContextSelector = createSelector(routerSelector, (router) => router.pageContext || {});
const currentRouteSelector = createSelector(routerSelector, (router) => router.currentRoute);
const previousRouteSelector = createSelector(routerSelector, (router) => router.previousRoute);
/**
 * The route blocking the redirections.
 */
const getBlockingRoute = createSelector(blockedRedirectionSelector, (blockedStatus) => blockedStatus?.blockingRoute);

const isRedirectionBlocked = createSelector(
    getBlockingRoute,
    (blockingRoute) => blockingRoute === window.location.href,
);

/**
 * Check if a page is currently loading.
 * A page is considered "loading"
 * * if the router is at "loading" status
 * * the current page ID is "loading-page", generally used for lazy loading fallbacks.
 */
const isPageLoading = createSelector(
    [currentPageSelector, routerIsLoading],
    (currentPage, routerLoading) => routerLoading || currentPage === LOADING_PAGE_ID,
);

export {
    routerSelector,
    routerStatusSelector,
    routerIsLoading,
    blockedRedirectionSelector,
    getBlockingRoute,
    isRedirectionBlocked,
    routerDataSelector,
    currentPageSelector,
    pageTitleSelector,
    currentRouteSelector,
    previousRouteSelector,
    isPageLoading,
    pageContextSelector,
};
